import React, { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { UploadCloud, X, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

const MAX_SIZE = 10 * 1024 * 1024
const ALLOWED = ["image/jpeg", "image/png", "image/gif", "image/webp"]

const Thumb = ({ src, name, onRemove, loading }) => (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    className="relative w-24"
  >
    <img
      src={src}
      alt={name}
      className="h-20 w-24 object-cover rounded-input border border-app-content-faint/20"
    />
    <button
      type="button"
      onClick={onRemove}
      disabled={loading}
      className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full bg-app-base-strong text-white shadow hover:opacity-80"
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
    </button>
    {name && <p className="mt-1 text-[10px] text-text-muted truncate text-center">{name}</p>}
  </motion.div>
)

const PhotoUploader = ({
  existing = [],
  files = [],
  onAddFiles,
  onRemoveFile,
  onDeleteExisting,
  deletingId,
}) => {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState("")

  // превью для новых файлов
  const previews = useMemo(
    () => files.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [files]
  )
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews])

  const handleFiles = (list) => {
    const picked = Array.from(list || [])
    const valid = picked.filter((f) => ALLOWED.includes(f.type) && f.size <= MAX_SIZE)
    setError(
      valid.length !== picked.length
        ? t("admin.photo_invalid", { defaultValue: "Допустимы JPG, PNG, GIF, WEBP до 10 МБ" })
        : ""
    )
    if (valid.length) onAddFiles(valid)
    if (inputRef.current) inputRef.current.value = ""
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          handleFiles(e.dataTransfer.files)
        }}
        className={`flex items-center justify-center gap-3 h-[106px] rounded-input border border-dashed cursor-pointer transition-colors ${
          dragOver
            ? "border-brand-main bg-brand-main/5"
            : "border-app-content-faint/40 bg-bg-secondary/40 hover:bg-bg-secondary"
        }`}
      >
        <UploadCloud className="w-8 h-8 text-text-muted" />
        <span className="text-text-secondary">
          {t("admin.drop_file_or", { defaultValue: "Перетащите файл или" })}{" "}
          <span className="text-brand-main font-medium">
            {t("admin.choose", { defaultValue: "выберите" })}
          </span>
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      <p className={`text-xs ${error ? "text-red-500" : "text-text-muted"}`}>
        {error || "JPG, PNG до 10 МБ"}
      </p>

      {(existing.length > 0 || previews.length > 0) && (
        <div className="flex flex-wrap gap-3">
          <AnimatePresence initial={false}>
            {existing.map((p) => (
              <Thumb
                key={`e-${p.id}`}
                src={p.photo_url}
                loading={deletingId === p.id}
                onRemove={() => onDeleteExisting(p.id)}
              />
            ))}
            {previews.map((p, i) => (
              <Thumb
                key={`n-${p.file.name}-${p.file.size}-${i}`}
                src={p.url}
                name={p.file.name}
                onRemove={() => onRemoveFile(i)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}

export default PhotoUploader
